import { List } from 'antd';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import useStaffListPageController from '../../1_application/setting/staffListPageController';
import { Staff, staffKindToDto } from '../../2_domain/staff/staff';
import { getIndex } from '../../lib/util/pagination';
import { SearchControlBoxContainer } from '../core/components/box/boxContainer';
import Column from '../core/components/box/column';
import Row from '../core/components/box/row';
import SizedBox from '../core/components/box/sizedBox';
import LXMiddleButton from '../core/components/buttons/lxMiddleButton';
import LXPageSizeSelector from '../core/components/buttons/lxPageSizeSelector';
import LXRefreshButton from '../core/components/buttons/lxRefreshButton';
import LXSearchButton from '../core/components/buttons/lxSearchButton';
import LXSelector from '../core/components/buttons/lxSelector';
import LXSortSelector from '../core/components/buttons/lxSortSelector';
import { XCenter } from '../core/components/center';
import LXList from '../core/components/list/lx_list';
import LXPagination from '../core/pagination/lxPagination';
import LXSearcher from '../core/texts/lxSearcher';
import { LXbody, LXCaption, LXh2 } from '../core/texts/lxText';

const HeaderPadding = styled.div`
  padding: 0px 10px;
`;

const ItemPadding = styled.div`
  padding: 12px 10px;
`;

const StaffListPage = () => {
  const theme = useTheme();
  const c = useStaffListPageController();
  const header = (
    <Row>
      <LXh2>{theme.staff.staffList}</LXh2>
      <SizedBox width={8}></SizedBox>
      <LXh2 fontWeight={400} color={theme.colors.caption}>
        {c.pageResult.totalElements}
      </LXh2>
    </Row>
  );

  const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') c.onSearch();
  };

  const searchBox = (
    <SearchControlBoxContainer>
      <Row justifyContent="center" alignItems="center">
        <LXSearcher
          width={320}
          value={c.searchText}
          onChange={c.setSearchText}
          placeholder={theme.texts.keywordSearch}
          onKeyDown={onPressEnter}
        ></LXSearcher>
        <SizedBox width={16}></SizedBox>
        <LXSelector<StaffKind | 'All'>
          value={c.staffKind}
          values={['All', 'Doctor', 'Nurse', 'Nonmedical Personnel']}
          labels={[
            theme.staff.all,
            theme.staff.doctor,
            theme.staff.nurse,
            theme.staff.manager,
          ]}
          onSelect={c.setStaffKind}
          dropdownItemWidth={240}
          dropdownHeaderWidth={240}
        ></LXSelector>
        <SizedBox width={40}></SizedBox>
        <LXSearchButton onClick={c.onSearch}></LXSearchButton>
        <SizedBox width={8}></SizedBox>
        <LXRefreshButton onClick={c.onRefresh}></LXRefreshButton>
      </Row>
    </SearchControlBoxContainer>
  );

  const listHeader = (
    <HeaderPadding>
      <Row alignItems="center">
        <LXCaption width={'44px'}>{theme.texts.no}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'128px'}>{theme.staff.staffNo}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'346px'}>{theme.texts.name}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'187px'}>{theme.staff.staffType}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'81px'}>{theme.texts.manage}</LXCaption>
      </Row>
    </HeaderPadding>
  );

  const listItemBuilder = (staff: Staff, idx: number) => (
    <List.Item>
      <ItemPadding>
        <Row alignItems="center">
          <LXbody width={'44px'}>
            {getIndex(c.pageResult.pageNumber, c.pagination.pageSize, idx)}
          </LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'128px'}>{staff.serial}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'346px'}>{staff.name}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'187px'}>
            {
              theme.staff[
                staffKindToDto(staff.kind) as keyof typeof theme.staff
              ]
            }
          </LXbody>
          <SizedBox width={16}></SizedBox>
          <LXSelector<'Edit' | 'Delete'>
            kind={'small'}
            placeholder={theme.texts.manage}
            values={['Edit', 'Delete']}
            labels={[theme.texts.edit, theme.texts.delete]}
            onSelect={(manageType) => c.onStaffManage(manageType, staff)}
            dropdownItemWidth={120}
          ></LXSelector>
        </Row>
      </ItemPadding>
    </List.Item>
  );

  return (
    <XCenter>
      <Column>
        <SizedBox height={80}></SizedBox>
        {header}
        <SizedBox height={40}></SizedBox>
        {searchBox}
        <SizedBox height={20}></SizedBox>
        <div style={{ alignSelf: 'flex-end' }}>
          <LXSortSelector
            value={c.pagination.isAscending ? 'Newest' : 'Oldest'}
            onSelect={c.setSortFilter}
          ></LXSortSelector>
        </div>
        <SizedBox height={16}></SizedBox>
        <LXList<Staff>
          datas={c.staffs}
          header={listHeader}
          itemBuilder={listItemBuilder}
          loading={c.isLoading}
        ></LXList>
        <SizedBox height={16}></SizedBox>
        <Row justifyContent="space-between" alignItems="center">
          <Row width="192px">
            <LXMiddleButton onClick={c.onNewStaffHandler}>
              {theme.texts.plusAddNew}
            </LXMiddleButton>
          </Row>
          <LXPagination
            current={c.pageResult.pageNumber}
            pageSize={c.pagination.pageSize}
            total={c.pageResult.totalElements}
            onChange={c.setPageNumber}
          ></LXPagination>
          <Row alignItems="center">
            <LXbody color={theme.colors.caption}>
              {theme.texts.rowsPerPage}
            </LXbody>
            <SizedBox width={10}></SizedBox>
            <LXPageSizeSelector
              value={c.pagination.pageSize}
              onSelect={c.setPageSize}
            ></LXPageSizeSelector>
          </Row>
        </Row>
      </Column>
    </XCenter>
  );
};

export default StaffListPage;
